import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { Protocol } from 'pmtiles'
import { getStyleUrl, DEFAULT_CENTER, DEFAULT_ZOOM, TILES_ATTRIBUTION_HTML } from '../utils/map-constants.js'

// Protocolo pmtiles:// — registrado UNA sola vez a nivel de módulo, antes de
// crear cualquier mapa. MapLibre delega en este handler la resolución de los
// rangos HTTP del archivo .pmtiles self-hosteado (un solo archivo estático,
// sin tile-server). Cuando el basemap es MapTiler (fallback) el protocolo
// simplemente no se usa — registrarlo es inofensivo.
const pmtilesProtocol = new Protocol()
maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile)

// setWorkerCount(2): default es Math.min(navigator.hardwareConcurrency, 4).
// En maquinas con muchos cores creaba 4 workers para tile loading. Con 22K
// features y 1 source unificado, 2 workers son suficientes y reducen el
// overhead de serializacion postMessage entre main thread y workers. El
// trace mostraba `postMessage` con 380 ms de self-time — bajar el conteo
// de workers reduce eso proporcionalmente sin afectar throughput de tiles
// (los tiles se procesan en paralelo igual, solo cambia el limite de
// concurrencia).
maplibregl.setWorkerCount(2)

export function createMap(container, options) {
  if (!options) options = {}
  var styleId = options.styleId || 'dark'
  var center = options.center || DEFAULT_CENTER
  var zoom = options.zoom || DEFAULT_ZOOM
  var apiKey = options.apiKey

  // Atribución toggleable por bandera de entorno. Las fuentes self-hosteadas
  // (OpenMapTiles + OSM + Overture) requieren crédito visible; se muestra en un
  // control compacto/colapsable solo si VITE_TILES_ATTRIBUTION === 'true'.
  var showAttribution = import.meta.env.VITE_TILES_ATTRIBUTION === 'true'

  var map = new maplibregl.Map({
    container: container,
    style: getStyleUrl(styleId, apiKey),
    center: center,
    zoom: zoom,
    attributionControl: showAttribution
      ? { compact: true, customAttribution: TILES_ATTRIBUTION_HTML }
      : false,
    // Opt-in: sin esto el canvas queda vacío tras cada frame y
    // getCanvas().toDataURL() devuelve una imagen en blanco. Solo lo activan
    // los mapas que necesitan exportar una captura (evidencia de medidas
    // cautelares); MapV2 no lo usa porque penaliza el render de ~22K features.
    preserveDrawingBuffer: options.preserveDrawingBuffer === true,
    // validateStyle: false en prod — MapLibre por default valida el style
    // spec en runtime durante el setStyle inicial. Es trabajo redundante
    // en builds prod (el style ya esta validado en dev). Ahorra 20-50 ms
    // en boot frio.
    validateStyle: !import.meta.env.PROD,
    // fadeDuration: 0 — eliminamos las transiciones de fade internas
    // de MapLibre. Heap snapshots en sesiones largas mostraron que cada
    // fade activo retiene un DOMTimer + un closure que captura referencias
    // al style spec completo (sky-color, atmosphere-blend, etc.). En
    // operación con mucho tráfico WS se acumulaban 400+ timers vivos y
    // 100K+ closures retenidos por imageManager, sumando 200-300 MB que
    // nunca se liberaban hasta recargar la pestaña.
    //
    // Trade-off visual: los iconos APARECEN/DESAPARECEN instantáneo al
    // entrar/salir del viewport, al cambiar zoom (icono ↔ cluster) y al
    // togglear capas. El symbol-sort-key del layer-manager fija ganadores
    // deterministas en colisiones, asi que NO hay flicker constante de
    // labels durante movimiento (que era la razon de tener fade).
    //
    // El movimiento suave de buses/devices NO se afecta — eso lo maneja
    // animationManager con rAF, no fadeDuration.
    fadeDuration: 0,
    maxZoom: 20,
    minZoom: 3,
    // Limita el tile cache out-of-view de MapLibre. Por default el cache
    // crece dinámicamente y puede acumular cientos de tiles con sus
    // vertex buffers en GPU, contribuyendo a varios cientos de MB en
    // GPU VRAM en sesiones largas. Con 100 tiles × 3 zoom levels el cap
    // queda en ~300 tiles activos, suficiente para navegación normal
    // sin penalizar el revisitado de zonas. Trade-off: más cache misses
    // al hacer pan rápido entre zonas distantes (re-fetch del tile).
    maxTileCacheSize: 100,
    maxTileCacheZoomLevels: 3,
  })

  return map
}

export function switchStyle(map, styleId, apiKey, onStyleReady) {
  map.setStyle(getStyleUrl(styleId, apiKey))
  map.once('style.load', function() {
    if (onStyleReady) onStyleReady()
  })
}
